<template>
    <main
        v-if="data"
        class="container relative pb-10 pt-25 md:pb-15 md:pt-36">
        <TheBreadcrumbs :items="breadcrumbs" />

        <section
            class="relative mt-5 flex flex-col justify-between gap-5 overflow-hidden rounded-20 p-3 md:flex-row md:gap-10 md:p-5">
            <div class="relative z-20 grow">
                <AtomTitle
                    level="1"
                    class="!text-20/120 md:!text-24/133 lg:!text-32/100">
                    {{ data?.data.name }}
                </AtomTitle>
                <div class="mt-2 space-x-2.5 font-medium text-accent-100">
                    <span
                        v-for="item in date"
                        :key="item">
                        {{ item }}
                    </span>
                </div>
                <div
                    v-if="data.data.preview_description"
                    v-html="data.data.preview_description"
                    class="tinyeditor mt-3 text-16/148 font-medium text-accent-100 md:mt-5" />
            </div>

            <picture
                class="relative z-20 aspect-video w-full grow rounded-12 object-cover md:max-w-[50%]">
                <source
                    v-if="data.data.image?.[0].webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(data.data.image[0].webp_url, 800) || ''
                    " />

                <source
                    v-else-if="
                        !data.data.image?.[0].webp_url &&
                        !data.data.image?.[0].url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />

                <img
                    :alt="
                        data.data.image?.[0].alt ||
                        data.data.name ||
                        'image not found'
                    "
                    :src="
                        getMediaLink(data.data.image?.[0].url || '', 800) ||
                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="aspect-video w-full rounded-12 object-cover" />
            </picture>

            <div class="absolute inset-0 z-10 size-full">
                <div class="bg-noise"></div>
            </div>
            <div
                class="absolute -bottom-[10rem] -left-[5rem] z-10 aspect-square w-[20rem] animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-300" />
            <div
                class="absolute -right-[5rem] -top-[10rem] z-10 aspect-square w-[20rem] animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500" />
            <div
                class="absolute -bottom-[5rem] left-1/2 z-10 aspect-square w-[10rem] -translate-x-1/2 animate-slow-pulse rounded-full bg-accent blur-[6rem] delay-500" />
        </section>

        <section class="mt-10 space-y-10 md:mt-15 md:space-y-15">
            <BlockCard
                v-for="block in data.data.blocks.data"
                :key="block.id"
                :block-id="block.id"
                :block-type="blockTypes[block.type_id]"
                :text="block.text"
                :image="block.image ? block.image[0] : null"
                :gallery-images="block.image ? block.image : []"
                :title="block.title"
                :autoplay-video="block.video ? block.video[0] : null"
                :link-rutube="block.link_rutube"
                :preview-rutube="
                    block.preview_video ? block.preview_video[0] : null
                " />
            <AtomHr class="!mt-10 md:!mt-20" />
        </section>

        <PortfolioProducts
            v-if="data.data.products.data.length"
            :title="data.data.recommended_title || 'Наша продукция'"
            :items="data.data.products.data"
            class="py-10 md:py-20" />
        <AtomHr />

        <NewsSection
            v-if="data?.data.read_also.data.length"
            title="Читайте также"
            hide-news-button
            :items="data.data.read_also.data"
            class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import PortfolioProducts from '~/components/pages/portfolio/portfolio-products/PortfolioProducts.vue'
import BlockCard from '~/components/particles/block-card/BlockCard.vue'
import type { TBlockType } from '~/components/particles/block-card/block-card.types'
import NewsSection from '~/components/particles/news-section/NewsSection.vue'
import type { INewsItem } from '~/components/particles/news-section/news-section.types'

import { formatPublicationDate } from '~/utils/publication-date'

const nuxtApp = useNuxtApp()
const modalStore = useModalStore()
const route = useRoute()

const { data, error } = await useApiFetch<Include<INewsItem>>(
    `/v1/news/${route.params.slug}`,
    {
        query: {
            include: ['blocks', 'products', 'read_also'].join(','),
        },
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        useLastModified: true,
    }
)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

const blockTypes: { [key: number]: TBlockType } = {
    1: 'text',
    2: 'video',
    3: 'gallery',
    5: 'text + image',
}

const date = computed<string[]>(() => {
    if (!data.value?.data.published_at) return []

    const [month, year] = formatPublicationDate(
        data.value.data.published_at,
        true
    ).split(' ')

    return [month, year]
})

const breadcrumbs = computed<BreadcrumbItem[]>(() => [
    {
        label: 'Cromi',
        to: '/',
    },
    {
        label: `Новости`,
        to: `/news/`,
    },
    {
        label: `${data.value?.data.name}`,
        to: `/news/${data.value?.data.slug}`,
    },
])

const organization = getSeoOrganizationReference()
if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })

    const image = data.value.data.image ? data.value.data.image[0] : null
    useSchemaOrg([
        defineWebPage({
            '@type': 'ItemPage',
        }),
        defineArticle({
            '@type': 'NewsArticle',
            headline: data.value.data.name,
            name: data.value.data.name,
            ...(data.value.data.preview_description && {
                description: stripHtml(data.value.data.preview_description),
            }),
            datePublished: data.value.data.published_at,
            articleBody: data.value.data.blocks.data
                .map((block) => {
                    return stripHtml(block.text) || ''
                })
                .join(' '),
            author: organization,
            publisher: organization,
            ...(image && {
                image: getMediaLink(image.url),
                thumbnailUrl: getMediaLink(image.url),
            }),
        }),
    ])
}
</script>
